<template>
  <div
    ref="anchor"
    class="section-menu-anchor position-relative"
    :class="{ 'mx-n2': isMobile }"
    :style="anchorStyle"
  >
    <div
      ref="sentinel"
      class="section-menu__sentinel"
      aria-hidden="true"
    />
    <div
      ref="root"
      data-qa="section_menu"
      class="section-menu bg-gray-0"
      :class="{ 'section-menu--pinned elevation-1': isPinned }"
      :style="pinnedStyle"
    >
      <div
        class="position-relative"
        :class="{ [containerClass]: !isMobile }"
      >
        <div
          class="section-menu__fog section-menu__fog_left"
          :class="{ 'section-menu__fog_visible': showLeftFog }"
        />
        <CoreSwiper
          ref="swiperRef"
          data-qa="section_menu_slider"
          free-mode
          slides-per-view="auto"
          :has-pagination="false"
          :space-between="0"
          :initial-slide="activeTabIndex ?? 0"
          :placeholder-min-height="52"
        >
          <template #slides="{ slideAttrs }">
            <div
              v-for="(item, index) in items"
              :key="index"
              v-bind="slideAttrs"
            >
              <div
                data-qa="section_menu_item"
                class="section-menu__tab text-none text-decoration-none d-inline-flex align-center cursor-pointer gc-2"
                :class="[
                  activeTabIndex === index ? 'text-subtitle-1 section-menu__tab--active' : 'text-body-1',
                  prevTabIndex !== undefined && activeTabIndex !== undefined && prevTabIndex <= activeTabIndex
                    ? 'section-menu__tab--left-to-right'
                    : 'section-menu__tab--right-to-left',
                ]"
                @click="handleSlideClick(item)"
              >
                <span
                  data-qa="section_menu_item_title"
                  class="text--text"
                >
                  {{ item.title }}
                </span>
                <span
                  v-if="item.count != null"
                  data-qa="section_menu_item_count"
                  class="text-subtitle-1 text-lpu-branding-color-primary"
                >
                  {{ item.count }}
                </span>
              </div>
            </div>
          </template>
        </CoreSwiper>
        <div
          class="section-menu__fog section-menu__fog_right"
          :class="{ 'section-menu__fog_visible': showRightFog }"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  computed,
  ref,
  watch,
} from 'vue'
import useIsMobile from 'src/composables/useIsMobile'
import Swiper from 'swiper/core'
import CoreSwiper from 'src/core/CoreSwiper/CoreSwiper.vue'
import useContextStore from 'src/store/useContextStore'
import type { Lpu } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_types'
import type { SectionMenuItem } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/SectionMenu/types'
import useSectionMenuItems from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/SectionMenu/composables/useSectionMenuItems'
import useSectionMenuScroll from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/SectionMenu/composables/useSectionMenuScroll'

type SwiperWithAllowClick = InstanceType<typeof Swiper> & {
  allowClick?: boolean
}

type CoreSwiperExpose = {
  swiperInstance: SwiperWithAllowClick | null
}

const props = defineProps<{
  lpu: Lpu
  synonymPricesCount?: number
  /**
   * URL страницы клиники. Если якоря секции нет в DOM, переход идёт на
   * `{lpuPageHref}{hash}` — так меню работает на отдельной странице прайса.
   */
  lpuPageHref?: string
  /**
   * Зафиксированный активный пункт (`id` из items).
   * На странице прайса — всегда «Цены», независимо от scroll-spy.
   */
  activeItemId?: string
}>()

const isMobile = useIsMobile()
const { context } = useContextStore()
const containerClass = computed(() => (
  context.request.feature_flags.ff_lpu_branding_page_v4
    ? 'container'
    : 'b-container'
))
const { items: sourceItems } = useSectionMenuItems(props)
const items = computed(() => {
  if (!props.lpuPageHref) {
    return sourceItems.value
  }

  return sourceItems.value.map(item => {
    if (item.id === 'prices' || !item.href?.startsWith('#')) {
      return item
    }

    return {
      ...item,
      redirectHref: `${props.lpuPageHref}${item.href}`,
    }
  })
})

const {
  root,
  anchor,
  sentinel,
  isPinned,
  anchorStyle,
  pinnedStyle,
  prevTabIndex,
  activeTabIndex: spyActiveTabIndex,
  handleTabClick,
} = useSectionMenuScroll(items)

const activeTabIndex = computed(() => {
  if (!props.activeItemId) {
    return spyActiveTabIndex.value
  }

  const index = items.value.findIndex(item => item.id === props.activeItemId)

  return index >= 0 ? index : spyActiveTabIndex.value
})

const swiperRef = ref<CoreSwiperExpose | null>(null)
const isAtStart = ref(true)
const isAtEnd = ref(false)

const showLeftFog = computed(() => !isAtStart.value)
const showRightFog = computed(() => !isAtEnd.value)

function getSwiper() {
  return swiperRef.value?.swiperInstance ?? null
}

function handleSlideClick(item: SectionMenuItem) {
  const swiper = getSwiper()

  // После драга Swiper гасит allowClick
  if (swiper?.allowClick === false) {
    return
  }

  handleTabClick(item)
}

watch(activeTabIndex, index => {
  if (typeof index !== 'number') {
    return
  }

  getSwiper()?.slideTo(index)
})

watch(
  () => getSwiper(),
  (swiper, _prev, onCleanup) => {
    if (!swiper) {
      return
    }

    const syncEdges = () => {
      isAtStart.value = swiper.isBeginning
      isAtEnd.value = swiper.isEnd
    }

    syncEdges()
    swiper.on('progress', syncEdges)
    swiper.on('resize', syncEdges)

    onCleanup(() => {
      swiper.off('progress', syncEdges)
      swiper.off('resize', syncEdges)
    })
  },
)
</script>

<style lang="scss" scoped>
@import '~www/themes/doctors/common/variables';

.section-menu {
  border-bottom: 1px solid $ui-kit-bg-gray-60;
  transition: top 0.3s cubic-bezier(0.25, 0.8, 0.5, 1),
    box-shadow 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
  will-change: top;

  &--pinned {
    position: fixed;
    left: 0;
    right: 0;
    z-index: 100;
    border-bottom: none;
  }

  &__sentinel {
    height: 0;
    width: 100%;
    pointer-events: none;
  }

  &__fog {
    pointer-events: none;
    z-index: 2;
    width: 0;
    position: absolute;
    top: 0;
    bottom: 0;
    transition: 0.1s linear;

    &_visible {
      width: 48px;
    }

    &_left {
      left: 0;
      background: linear-gradient(
        90deg,
        #FFFFFF 0.71%,
        rgba(255, 255, 255, 0.8099) 11.66%,
        rgba(255, 255, 255, 0.6198) 21.74%,
        rgba(255, 255, 255, 0.3843) 31.54%,
        rgba(255, 255, 255, 0) 42.77%
      );
    }

    &_right {
      right: 0;
      background: linear-gradient(
        270deg,
        #FFFFFF 0.71%,
        rgba(255, 255, 255, 0.8099) 11.66%,
        rgba(255, 255, 255, 0.6198) 21.74%,
        rgba(255, 255, 255, 0.3843) 31.54%,
        rgba(255, 255, 255, 0) 42.77%
      );
    }
  }

  &__tab {
    position: relative;
    height: 52px;
    letter-spacing: normal;
    padding: 16px 24px;
    white-space: nowrap;
    outline: none;
    user-select: none;

    // Как у Vuetify .v-tab: затемнение currentColor на hover/focus
    &::before {
      content: '';
      position: absolute;
      inset: 0;
      background-color: currentColor;
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
    }

    &:hover::before {
      opacity: 0.04;
    }

    &:focus::before {
      opacity: 0.12;
    }

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 2px;
      background-color: var(--lpu-branding-color-primary);
      transition: transform 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
      width: 100%;
      transform: scaleX(0);
    }

    &--active {
      color: var(--lpu-branding-color-primary);

      &::after {
        transform: scaleX(1);
      }

      &:hover::before {
        opacity: 0.12;
      }

      &:focus::before {
        opacity: 0.16;
      }
    }

    &--left-to-right::after {
      transform-origin: right;
    }

    &--left-to-right#{&}--active::after {
      transform-origin: left;
    }

    &--right-to-left::after {
      transform-origin: left;
    }

    &--right-to-left#{&}--active::after {
      transform-origin: right;
    }
  }
}
</style>
